import React from 'react';
import timexBanner from './assets/FluX Banner weiß transparent.png';
import timexDashboard from './assets/FluX Web Dashboard.png';
import timexCalendarMenu from './assets/FluX Calendar Menu.png';
import timexProjectsMenu from './assets/FluX Projects Menu.png';
import timexTasksMenu from './assets/FluX Web Projects.png';
import timexBotDashboard from './assets/FluX Bot Dashboard.png';
import timexBotCalendar from './assets/FluX Bot Calendar.png';
import timexBotTimeEntries from './assets/FluX Bot Time Entries.png';
import timexBotProjects from './assets/FluX Bot Projects.png';

const App: React.FC = () => {
  const [isCommandsOpen, setIsCommandsOpen] = React.useState(false);
  const [isDashboardPrimary, setIsDashboardPrimary] = React.useState(false);
  const [activeScreenshot, setActiveScreenshot] = React.useState<{
    src: string;
    alt: string;
    label: string;
  } | null>(null);
  const [cardTilt, setCardTilt] = React.useState({ bot: { x: 0, y: 0 }, web: { x: 0, y: 0 } });
  const [visibleSections, setVisibleSections] = React.useState<Set<string>>(new Set());

  // Intersection Observer für Scroll-Animationen
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const sections = document.querySelectorAll('[data-scroll-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  // 3D-Tilt-Handler für Hero-Cards
  const handleBotCardMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 10;
    const rotateY = (centerX - x) / 10;
    setCardTilt((prev) => ({ ...prev, bot: { x: rotateX, y: rotateY } }));
  };

  const handleBotCardMouseLeave = () => {
    setCardTilt((prev) => ({ ...prev, bot: { x: 0, y: 0 } }));
  };

  const handleWebCardMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 10;
    const rotateY = (centerX - x) / 10;
    setCardTilt((prev) => ({ ...prev, web: { x: rotateX, y: rotateY } }));
  };

  const handleWebCardMouseLeave = () => {
    setCardTilt((prev) => ({ ...prev, web: { x: 0, y: 0 } }));
  };

  return (
    <div className="app">
      <header className="hero">
        <div className="hero-content">
          <img src={timexBanner} alt="FluX logo" className="hero-banner" />
          <h1>
            Time tracking for Discord teams –
            <span className="accent"> Bot & Web Dashboard</span>
          </h1>
          <p className="subtitle">
            FluX combines a powerful Discord bot with a modern web dashboard.
            Track working hours, manage projects and absences – right inside your server and in the browser.
          </p>
          <div className="hero-actions">
            <a
              href="https://discord.com/oauth2/authorize?client_id=1447896437634629694"
              className="btn primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Invite Discord Bot
            </a>
            <a
              href="https://flux-time.com"
              className="btn ghost"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Web Dashboard
            </a>
          </div>
          <div className="hero-meta">
            <span>✅ Time tracking & Vacations</span>
            <span>✅ Projects & Tasks</span>
            <span>✅ Calendar & Reporting</span>
            <span>✅ Roles & Granular Permissions</span>
          </div>
        </div>
        <div className="hero-visual">
          <div className="card-stack">
            <div
              className={`card glass ${isDashboardPrimary ? 'secondary' : 'primary'}`}
              onClick={() => setIsDashboardPrimary(false)}
              onMouseMove={handleBotCardMouseMove}
              onMouseLeave={handleBotCardMouseLeave}
              style={{
                transform: isDashboardPrimary
                  ? `rotate(6deg) translate(18px, 28px) perspective(1000px) rotateX(${cardTilt.bot.x}deg) rotateY(${cardTilt.bot.y}deg)`
                  : `rotate(-3deg) translateY(4px) perspective(1000px) rotateX(${cardTilt.bot.x}deg) rotateY(${cardTilt.bot.y}deg)`,
              }}
            >
              <h3>Discord Bot</h3>
              <p>Slash commands for quick setup, time tracking, projects and tasks – all inside Discord.</p>
              <ul>
                <li>/setup – onboard your server and create system roles & channels</li>
                <li>/menu – open the FluX main menu with actions and shortcuts</li>
                <li>Further actions via buttons for projects, tasks and time tracking</li>
              </ul>
            </div>
            <div
              className={`card glass ${isDashboardPrimary ? 'primary' : 'secondary'}`}
              onClick={() => setIsDashboardPrimary(true)}
              onMouseMove={handleWebCardMouseMove}
              onMouseLeave={handleWebCardMouseLeave}
              style={{
                transform: isDashboardPrimary
                  ? `rotate(-3deg) translateY(4px) perspective(1000px) rotateX(${cardTilt.web.x}deg) rotateY(${cardTilt.web.y}deg)`
                  : `rotate(6deg) translate(18px, 28px) perspective(1000px) rotateX(${cardTilt.web.x}deg) rotateY(${cardTilt.web.y}deg)`,
              }}
            >
              <h3>Web Dashboard</h3>
              <p>Analytics, calendars, roles and permissions for admins and managers.</p>
              <ul>
                <li>Project Management</li>
                <li>Task Management</li>
                <li>Time Tracking</li>
                <li>Absence Management</li>
                <li>Role & Permission Management</li>
                <li>Reports</li>
                <li>and more...</li>
              </ul>
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        <section className="grid">
          <article
            className={`feature-card ${visibleSections.has('bot-features') ? 'scroll-visible' : ''}`}
            id="bot-features"
            data-scroll-animate
          >
            <h2>Bot Features</h2>
            <p>
              The FluX bot brings everyday workflows directly into your Discord server.
              Intuitive slash commands make time tracking and absences fast and transparent.
            </p>
            <div className="feature-list">
              <div>
                <h3>Time tracking</h3>
                <ul>
                  <li>One-click time tracking with buttons</li>
                  <li>Support for breaks & project assignment</li>
                  <li>Live status & active sessions</li>
                </ul>
              </div>
              <div>
                <h3>Absences</h3>
                <ul>
                  <li>Vacation & absence requests in your channels</li>
                  <li>Approval workflow for managers</li>
                  <li>Automatic updates in the calendar</li>
                </ul>
              </div>
            </div>
          </article>

          <article
            className={`feature-card ${visibleSections.has('web-dashboard') ? 'scroll-visible' : ''}`}
            id="web-dashboard"
            data-scroll-animate
          >
            <h2>Web Dashboard</h2>
            <p>
              The web interface extends the bot with analytics, administration and a comfortable
              management UI. Everything is linked directly to the bot tables in the database.
            </p>
            <div className="feature-list">
              <div>
                <h3>Projects & Tasks</h3>
                <ul>
                  <li>Project overview with status & members</li>
                  <li>Tasks with priorities & due dates</li>
                  <li>Assignments to Discord users</li>
                </ul>
              </div>
              <div>
                <h3>Absences & Overtime</h3>
                <ul>
                  <li>Vacation management with approval flow</li>
                  <li>Weekend & overtime calculation</li>
                  <li>Exportable reports</li>
                </ul>
              </div>
            </div>
          </article>
        </section>

        <section className="flow">
          <h2>How bot & website work together</h2>
          <div className="flow-steps">
            <div
              className={`flow-step ${visibleSections.has('flow-step-1') ? 'scroll-visible' : ''}`}
              id="flow-step-1"
              data-scroll-animate
            >
              <span className="step-tag">1 · Discord</span>
              <h3>Action in the server</h3>
              <p>
                Team members use slash commands to track time, pick projects or submit absence requests.
              </p>
            </div>
            <div
              className={`flow-step ${visibleSections.has('flow-step-2') ? 'scroll-visible' : ''}`}
              id="flow-step-2"
              data-scroll-animate
            >
              <span className="step-tag">2 · FluX DB</span>
              <h3>Shared data layer</h3>
              <p>
                The bot writes directly into the <code>database*</code> tables. The website reads and
                manages exactly the same data.
              </p>
            </div>
            <div
              className={`flow-step ${visibleSections.has('flow-step-3') ? 'scroll-visible' : ''}`}
              id="flow-step-3"
              data-scroll-animate
            >
              <span className="step-tag">3 · Web Dashboard</span>
              <h3>Visualisation & administration</h3>
              <p>
                Managers see analytics, manage roles & permissions and configure menus per server.
              </p>
            </div>
          </div>
        </section>

        <section className="permissions">
          <h2>Roles & permissions</h2>
          <p className="permissions-text">
            A central permission system connects Discord roles, FluX roles and web permissions.
            Admin, Manager, Accounting and Employee profiles get exactly the access they need.
          </p>
          <div className="permissions-grid">
            <div className="role-column">
              <h3>Default roles</h3>
              <ul>
                <li>Administrator</li>
                <li>Manager</li>
                <li>Accounting</li>
                <li>Employee</li>
                <li>Support for custom roles</li>
              </ul>
            </div>
            <div className="role-column">
              <h3>Example permissions</h3>
              <ul>
                <li>projects.manage · manage projects</li>
                <li>time_tracking.manage · review working hours</li>
                <li>vacation.manage · approve vacation requests</li>
                <li>system.roles · configure roles & rights</li>
              </ul>
            </div>
            <div className="role-column">
              <h3>Synchronised</h3>
              <ul>
                <li>Web UI & bot share the same tables</li>
                <li>Granular permissions per guild/server</li>
                <li>Extendable with custom roles</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="screenshots">
          <h2>How it looks in Discord & in the browser</h2>
          <p className="screenshots-text">
            Real screenshots from the FluX web dashboard.
          </p>
          <div className="screenshot-grid">
            <div className="screenshot-card">
              <div className="screenshot-label">Web Dashboard · Overview</div>
              <button
                type="button"
                className="screenshot-window"
                onClick={() =>
                  setActiveScreenshot({
                    src: timexDashboard,
                    alt: 'FluX web dashboard overview',
                    label: 'Web Dashboard · Overview',
                  })
                }
              >
                <img
                  src={timexDashboard}
                  alt="FluX web dashboard overview"
                  className="screenshot-image"
                />
              </button>
            </div>

            <div className="screenshot-card">
              <div className="screenshot-label">Web Dashboard · Calendar</div>
              <button
                type="button"
                className="screenshot-window"
                onClick={() =>
                  setActiveScreenshot({
                    src: timexCalendarMenu,
                    alt: 'FluX calendar view with absences',
                    label: 'Web Dashboard · Calendar',
                  })
                }
              >
                <img
                  src={timexCalendarMenu}
                  alt="FluX calendar view with absences"
                  className="screenshot-image"
                />
              </button>
            </div>

            <div className="screenshot-card">
              <div className="screenshot-label">Web Dashboard · Time Entries</div>
              <button
                type="button"
                className="screenshot-window"
                onClick={() =>
                  setActiveScreenshot({
                    src: timexProjectsMenu,
                    alt: 'FluX projects overview',
                    label: 'Web Dashboard · Time Entries',
                  })
                }
              >
                <img
                  src={timexProjectsMenu}
                  alt="FluX projects overview"
                  className="screenshot-image"
                />
              </button>
            </div>

            <div className="screenshot-card">
              <div className="screenshot-label">Web Dashboard · Projects</div>
              <button
                type="button"
                className="screenshot-window"
                onClick={() =>
                  setActiveScreenshot({
                    src: timexTasksMenu,
                    alt: 'FluX tasks and priorities',
                    label: 'Web Dashboard · Projects',
                  })
                }
              >
                <img
                  src={timexTasksMenu}
                  alt="FluX tasks and priorities"
                  className="screenshot-image"
                />
              </button>
            </div>
          </div>

          <h3 className="screenshots-subtitle">Discord Bot views</h3>
          <div className="screenshot-grid">
            <div className="screenshot-card">
              <div className="screenshot-label">Bot · Overview</div>
              <button
                type="button"
                className="screenshot-window"
                onClick={() =>
                  setActiveScreenshot({
                    src: timexBotDashboard,
                    alt: 'FluX bot overview in Discord',
                    label: 'Bot · Overview',
                  })
                }
              >
                <img
                  src={timexBotDashboard}
                  alt="FluX bot overview in Discord"
                  className="screenshot-image"
                />
              </button>
            </div>

            <div className="screenshot-card">
              <div className="screenshot-label">Bot · Calendar</div>
              <button
                type="button"
                className="screenshot-window"
                onClick={() =>
                  setActiveScreenshot({
                    src: timexBotCalendar,
                    alt: 'FluX bot calendar view',
                    label: 'Bot · Calendar',
                  })
                }
              >
                <img
                  src={timexBotCalendar}
                  alt="FluX bot calendar view"
                  className="screenshot-image"
                />
              </button>
            </div>

            <div className="screenshot-card">
              <div className="screenshot-label">Bot · Time Entries</div>
              <button
                type="button"
                className="screenshot-window"
                onClick={() =>
                  setActiveScreenshot({
                    src: timexBotTimeEntries,
                    alt: 'FluX bot time entries view',
                    label: 'Bot · Time Entries',
                  })
                }
              >
                <img
                  src={timexBotTimeEntries}
                  alt="FluX bot time entries view"
                  className="screenshot-image"
                />
              </button>
            </div>

            <div className="screenshot-card">
              <div className="screenshot-label">Bot · Projects</div>
              <button
                type="button"
                className="screenshot-window"
                onClick={() =>
                  setActiveScreenshot({
                    src: timexBotProjects,
                    alt: 'FluX bot projects view',
                    label: 'Bot · Projects',
                  })
                }
              >
                <img
                  src={timexBotProjects}
                  alt="FluX bot projects view"
                  className="screenshot-image"
                />
              </button>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="cta-card">
            <h2>Ready for your next Discord workspace?</h2>
            <p>
              Use FluX as the central hub for working hours, projects and team planning – in chat and in the browser.
            </p>
            <div className="hero-actions">
              <a
                href="https://flux-time.com"
                className="btn ghost"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Dashboard
              </a>
              <button
                type="button"
                className="btn ghost"
                onClick={() => setIsCommandsOpen(true)}
              >
                View Bot Commands
              </button>
              <a
                href="https://discord.gg/v9uSb9xayx"
                className="btn secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Need help?
              </a>
            </div>
          </div>
        </section>
      </main>

      {isCommandsOpen && (
        <div
          className="modal-backdrop"
          onClick={() => setIsCommandsOpen(false)}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Discord Bot Commands</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setIsCommandsOpen(false)}
              >
                ×
              </button>
            </div>
            <p className="modal-intro">
              FluX is intentionally simple – you mainly need these two slash commands:
            </p>
            <ul className="modal-list">
              <li>
                <code>/setup</code> – start the onboarding flow and connect your Discord server with FluX
              </li>
              <li>
                <code>/menu</code> – open the FluX menu with all available actions and shortcuts
              </li>
            </ul>
          </div>
        </div>
      )}

      {activeScreenshot && (
        <div
          className="screenshot-lightbox-backdrop"
          onClick={() => setActiveScreenshot(null)}
        >
          <div
            className="screenshot-lightbox"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="screenshot-lightbox-header">
              <span>{activeScreenshot.label}</span>
              <button
                type="button"
                className="screenshot-lightbox-close"
                onClick={() => setActiveScreenshot(null)}
              >
                ×
              </button>
            </div>
            <div className="screenshot-lightbox-body">
              <img
                src={activeScreenshot.src}
                alt={activeScreenshot.alt}
                className="screenshot-lightbox-image"
              />
            </div>
          </div>
        </div>
      )}

      <footer className="footer">
        <span>FluX · Discord Bot & Web Dashboard</span>
        {/* <span>Built with React & Vite</span> */}
      </footer>
    </div>
  );
};

export default App;


